/**
 * Spec 088c — Lazy-loaded `react-force-graph-2d` proxy.
 *
 * The force-graph runtime is ~90KB gzipped and depends on canvas. Eager-
 * loading it into the main bundle would slow first-paint on every route,
 * including routes that never render a graph. This spec introduces the
 * lazy-loading pattern; downstream specs 086/087 (cluster map, coverage)
 * will use it via the real consumer routes.
 *
 * Gate 088c.C asserts: the entry chunk does NOT contain
 * `react-force-graph-2d`; at least one lazy-loaded chunk does.
 *
 * Usage (in a route-level page):
 *   import { LazyForceGraph } from '../components/LazyForceGraph';
 *   // ...
 *   <LazyForceGraph graphData={...} />
 *
 * Suspense is provided by this wrapper so consumers don't have to.
 */
import { lazy, Suspense } from 'react';

// Vite's code-splitter emits a separate chunk for this dynamic import,
// regardless of whether the component is currently mounted. The string
// "react-force-graph-2d" will NOT appear in the main entry chunk.
const ForceGraph2D = lazy(() => import('react-force-graph-2d'));

export interface LazyForceGraphProps {
  graphData?: { nodes: unknown[]; links: unknown[] };
  width?: number;
  height?: number;
  [key: string]: unknown;
}

function GraphSkeleton({ width = 640, height = 480 }: { width?: number; height?: number }) {
  return (
    <div
      data-testid="force-graph-skeleton"
      style={{ width, height }}
      className="animate-pulse rounded-lg border border-slate-200 bg-slate-100"
      aria-label="Loading graph"
    />
  );
}

export function LazyForceGraph(props: LazyForceGraphProps) {
  return (
    <Suspense fallback={<GraphSkeleton width={props.width} height={props.height} />}>
      <ForceGraph2D {...props} />
    </Suspense>
  );
}

export default LazyForceGraph;
